/**
 * SignaturePad — touch / mouse capture on an HTML canvas, exports PNG data URL.
 * Lightweight, no external dep.
 */
import React, { useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { Eraser } from "lucide-react";

const PEN_COLOR = "#111827";
const PEN_WIDTH = 2.2;
const CANVAS_W = 360;
const CANVAS_H = 140;

export default function SignaturePad({ label, value, onChange, testId }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);

  // Replay an existing dataURL into the canvas on mount / value change.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    if (!value) return;
    const img = new Image();
    img.onload = () => ctx.drawImage(img, 0, 0, c.width, c.height);
    img.src = value;
  }, [value]);

  const pos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - rect.left) * (c.width / rect.width),
             y: (t.clientY - rect.top) * (c.height / rect.height) };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pos(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const p = pos(e);
    ctx.strokeStyle = PEN_COLOR;
    ctx.lineWidth = PEN_WIDTH;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const c = canvasRef.current;
    onChange?.(c.toDataURL("image/png"));
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, c.width, c.height);
    onChange?.(null);
  };

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-600">{label}</div>
        <button
          type="button"
          onClick={clear}
          className="text-[10px] uppercase tracking-wider font-bold text-slate-500 hover:text-red-600 flex items-center gap-1"
          data-testid={`${testId}-clear`}
        >
          <Eraser className="w-3 h-3" /> Clear
        </button>
      </div>
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        className="border-2 border-dashed border-slate-300 rounded-md w-full bg-white touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid={`${testId}-canvas`}
      />
    </div>
  );
}
